import { useState } from 'react';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectItem,
  Textarea,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import type { CommitmentNote } from 'common/src/utils/types';
import { useTranslation } from 'common/src/hooks/useTranslation';

interface NotesPopoverProps {
  receiverName: string;
  notes: CommitmentNote[];
  weeks: number[];
  canEdit: boolean;
  onAdd: (week: number | null, content: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}

const GENERAL = 'general';

/** Notas de un recibidor, generales o de una semana. */
export default function NotesPopover({
  receiverName,
  notes,
  weeks,
  canEdit,
  onAdd,
  onDelete,
}: NotesPopoverProps) {
  const { t } = useTranslation('commitments');
  const [week, setWeek] = useState<string>(GENERAL);
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      await onAdd(week === GENERAL ? null : Number(week), content.trim());
      setContent('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover placement='right-start'>
      <PopoverTrigger>
        <button
          type='button'
          aria-label={t('Notas de {{name}}', { name: receiverName })}
          className={`relative inline-flex shrink-0 rounded p-1 hover:bg-default-100 ${
            notes.length ? 'text-primary' : 'text-default-300'
          }`}
        >
          <Icon
            icon={notes.length ? 'tabler:message-2-filled' : 'tabler:message-2'}
            width={16}
          />
          {notes.length > 0 && (
            <span className='absolute -right-1 -top-1 rounded-full bg-primary px-1 text-[10px] leading-4 text-primary-foreground'>
              {notes.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-80 p-3'>
        <div className='w-full'>
          <p className='mb-2 text-sm font-semibold'>{receiverName}</p>
          {notes.length === 0 ? (
            <p className='mb-3 text-xs text-default-400'>
              {t('Sin notas todavía.')}
            </p>
          ) : (
            <div className='mb-3 flex max-h-56 flex-col gap-2 overflow-y-auto'>
              {notes.map((note) => (
                <div
                  key={note.id}
                  className='rounded-lg bg-default-100 p-2 text-sm'
                >
                  <div className='mb-0.5 flex items-center justify-between gap-2 text-xs text-default-500'>
                    <span>
                      {note.week == null
                        ? t('General')
                        : `${t('Semana')} ${note.week}`}
                      {note.author ? ` · ${note.author}` : ''}
                    </span>
                    {canEdit && (
                      <button
                        type='button'
                        aria-label={t('Borrar nota')}
                        onClick={() => onDelete(note.id)}
                        className='text-default-400 hover:text-danger'
                      >
                        <Icon icon='tabler:trash' width={14} />
                      </button>
                    )}
                  </div>
                  <p className='whitespace-pre-wrap'>{note.content}</p>
                </div>
              ))}
            </div>
          )}
          {canEdit && (
            <div className='flex flex-col gap-2'>
              <Select
                size='sm'
                label={t('Para')}
                selectedKeys={[week]}
                onSelectionChange={(keys) =>
                  setWeek(String(Array.from(keys)[0] ?? GENERAL))
                }
              >
                {[GENERAL, ...weeks.map(String)].map((w) => (
                  <SelectItem key={w} value={w}>
                    {w === GENERAL
                      ? t('Todo el programa del cliente')
                      : `${t('Semana')} ${w}`}
                  </SelectItem>
                ))}
              </Select>
              <Textarea
                size='sm'
                minRows={2}
                placeholder={t('Ej. solo lleva fruta XL')}
                value={content}
                onValueChange={setContent}
              />
              <Button
                size='sm'
                color='primary'
                onPress={handleAdd}
                isLoading={saving}
                isDisabled={!content.trim()}
              >
                {t('Agregar nota')}
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
